import { useEffect, useState } from 'react'
import {
  Dialog,
  DialogTitle,
  Button,
  DialogContent,
  TableCell,
  TableRow,
  TableBody,
  Table,
  Checkbox,
  IconButton,
  Typography,
  Autocomplete,
} from '@mui/material'
import { Box, TableContainer, TableHead, TextField } from '@material-ui/core'
import { useNotify, useRecordContext, useRefresh } from 'react-admin'
import ClearIcon from '@material-ui/icons/Clear'
import CloseIcon from '@mui/icons-material/Close'
import { Save } from '@material-ui/icons'
import Api from '../../Apis/api.service'

export const RegistrationModal = ({ open, onClose }) => {
  const record = useRecordContext()
  const refresh = useRefresh()
  const notify = useNotify()

  const {
    coproprietaires_absents: absentCoowners,
    coproprietaires: coowners,
    id: agId,
  } = record
  const [formData, setFormData] = useState({})

  useEffect(() => {
    setFormData(
      absentCoowners.reduce((acc, { id }) => {
        return { ...acc, [id]: { id, isPresent: false, representant: '' } }
      }, {})
    )
  }, [absentCoowners])

  const [searchTerm, setSearchTerm] = useState('')

  const handleSearch = (event) => {
    setSearchTerm(event.target.value)
  }

  const handleClearSearch = () => {
    setSearchTerm('')
  }

  const filteredCoowners = absentCoowners.filter((coowner) =>
    coowner.libelle.toLowerCase().includes(searchTerm.toLowerCase())
  )

  const handleChange = (id, type, value) => {
    const existingData = formData[id]
    if (existingData) {
      const newData = {
        [id]: { ...existingData, [type]: value },
      }
      setFormData((prevForm) => ({ ...prevForm, ...newData }))
    }
  }

  const handleSelectAll = (isPresent) => {
    setFormData((prevFormData) => {
      const updatedFormData = { ...prevFormData }
      Object.keys(updatedFormData).forEach((id) => {
        updatedFormData[id] = {
          ...updatedFormData[id],
          isPresent: isPresent,
        }
      })
      return updatedFormData
    })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    const filteredData = Object.values(formData).reduce((acc, coowner) => {
      if (coowner.isPresent) {
        return [
          ...acc,
          {
            ...coowner,
            representant:
              coowner.representant !== '' ? coowner.representant : null,
          },
        ]
      }
      return acc
    }, [])
    try {
      await Api.ags.registerCoowners(agId, filteredData)
      refresh()
      notify('Copropriétaires enregistrés avec succès !', { type: 'success' })
    } catch (error) {
      notify("Erreur lors de l'enregistrement des copropriétaires !", {
        type: 'error',
      })
    }

    onClose()
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        style: {
          minWidth: '70%',
          minHeight: '-webkit-fill-available',
        },
      }}
    >
      <DialogTitle>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 4,
          }}
        >
          <Typography variant={'h5'}>
            Enregistrement des Copropriétaires
          </Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2,
            mt: 1,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <TextField
              label="Recherche"
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={handleSearch}
            />
            {searchTerm ? (
              <Button
                variant="outlined"
                color="primary"
                startIcon={<ClearIcon />}
                onClick={handleClearSearch}
                sx={{ ml: 1 }}
              >
                Retour à la liste
              </Button>
            ) : (
              <Button
                type="submit"
                variant={'contained'}
                onClick={handleSubmit}
                endIcon={<Save />}
                sx={{ ml: 1 }}
              >
                Enregistrer
              </Button>
            )}
          </Box>
          <Box sx={{ display: 'flex' }}>
            <Button
              variant="contained"
              color="secondary"
              onClick={() => handleSelectAll(true)}
              sx={{ mr: 1 }}
            >
              Tous présent
            </Button>
            <Button
              variant="contained"
              color="secondary"
              onClick={() => handleSelectAll(false)}
            >
              Tous absent
            </Button>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Copropriétaire</TableCell>
                <TableCell>Présent</TableCell>
                <TableCell>Représenté par</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredCoowners.map(({ id, libelle }) => (
                <TableRow key={id}>
                  <TableCell>{libelle}</TableCell>
                  <TableCell>
                    <Checkbox
                      checked={formData[id]?.isPresent}
                      onChange={(e) =>
                        handleChange(id, 'isPresent', e.target.checked)
                      }
                    />
                  </TableCell>
                  <TableCell width={'30%'}>
                    <Autocomplete
                      sx={{ width: '80%' }}
                      value={formData[id]?.representant || null}
                      onChange={(event, newValue) => {
                        if (newValue && newValue.inputValue) {
                          handleChange(id, 'representant', newValue.inputValue)
                        } else {
                          handleChange(id, 'representant', newValue.libelle)
                        }
                      }}
                      filterOptions={(options, params) => {
                        const filtered = options.filter((option) =>
                          option.libelle
                            .toLowerCase()
                            .includes(params.inputValue.toLowerCase())
                        )

                        if (params.inputValue !== '') {
                          filtered.push({
                            inputValue: params.inputValue,
                            libelle: `Ajouter "${params.inputValue}"`,
                          })
                        }

                        return filtered
                      }}
                      selectOnFocus
                      clearOnBlur
                      handleHomeEndKeys
                      options={coowners.filter((coowner) => coowner.id !== id)}
                      getOptionLabel={(option) => {
                        if (typeof option === 'string') {
                          return option
                        }
                        return option.libelle
                      }}
                      renderOption={(props, option) => (
                        <li {...props}>{option.libelle}</li>
                      )}
                      freeSolo
                      renderInput={(params) => (
                        <TextField {...params} variant="outlined" />
                      )}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
    </Dialog>
  )
}
